import React from "react";
import { BrowserRouter, Route, Routes, Navigate, useLocation, useParams } from "react-router-dom";

import LandingPage from "../Pages/LandingPage";
import Login from "../Pages/Login";
import Register from "../Pages/Register";
import Dashboard from "../Pages/Dashboard";
import CreateUser from "../Pages/CreateUser";
import UserManagement from "../Pages/UserManagement";
import CustomerManagement from "../Pages/CustomerManagement";
import UpdatePassword from "../Pages/UpdatePassword";
import TestYubiKey from "../Pages/TestYubiKey";
import PanelType from "../Pages/PanelType";
import RegisterYubiKey from "../Pages/RegisterYubiKey";
import YubiKeyDashboard from "../Pages/YubiKeyDashboard";
import VeloxLogs from "../Pages/VeloxLogs";
import Messages from "../Pages/Messages";
import MDAReview from "../Pages/MDAReview";
import AssignYubiKey from "../Pages/AssignYubiKey";
import MDLandingPage from "../Pages/MDLandingPage";
import MDLogin from "../Pages/MDLogin";
import MDDashboard from "../Pages/MDDashboard";
import MDUpdatePassword from "../Pages/MDUpdatePassword";
import MDYubiKeyDashboard from "../Pages/MDYubiKeyDashboard";
import MDCreateT from "../Pages/MDCreateT";
import MDCreateSI from "../Pages/MDCreateSI";
import MDLogs from "../Pages/MDLogs";
import MDManagement from "../Pages/MDManagement";
import MDSIManagement from "../Pages/MDSIManagement";
import MDPanelType from "../Pages/MDPanelType";
import MDRequestKey from "../Pages/MDRequestKey";
import MDAssignKey from "../Pages/MDAssignKey";

// const isProductionEnvironment = process.env.IS_PRODUCTION_ENVIRONMENT === "true";

// const basePath = isProductionEnvironment ? "/mp" : "/test";
const basePath = "";

const isVELOXAuthenticated = () => {
  return localStorage.getItem('@user') === "true";
};

const isCustomerAuthenticated = () => {
  return localStorage.getItem('@customer') === "true";
}

function RedirectToLogin() {
  const loc = useLocation();
  const next = encodeURIComponent(loc.pathname + loc.search);

  // Normalize basePath ('', '/mp', etc.)
  const prefix = basePath ? `/${basePath.replace(/^\/|\/$/g, "")}` : "";

  // Strip prefix and get first URL segment
  const rest = loc.pathname.startsWith(prefix + "/")
    ? loc.pathname.slice(prefix.length + 1)
    : loc.pathname.replace(/^\//, "");
  const firstSeg = (rest.split("/")[0] || "").trim();

  // Pages that are NOT mdId namespaces — always use staff login
  const topLevelPages = new Set([
    "login",
    "register",
    "dashboard",
    "messages",
    "mda-review",
    "assign-yubikey",
    "velox-logs",
    "test-yubikey",
    "commissioningtool",
    "register-yubikey",
    "customer-management",
    "yubikey_dashboard",
    "create-user",
    "user-management",
    "update_password",
  ]);

  if (topLevelPages.has(firstSeg)) {
    // Staff/internal login flow
    return <Navigate to={`${prefix}/login?next=${next}`} replace />;
  }

  if (firstSeg) {
    // Treat the first segment as an mdId → MD customer login
    return <Navigate to={`${prefix}/${firstSeg}/login?next=${next}`} replace />;
  }

  // Fallback
  return <Navigate to={`${prefix}/login?next=${next}`} replace />;
}

function MDLoginGate() {
  const { mdId } = useParams();

  const isCustomer = isCustomerAuthenticated();
  const hasEmail = !!localStorage.getItem("userEmail");

  // If state looks valid, go straight to MD dashboard
  if (isCustomer && hasEmail) {
    return <Navigate to={`${basePath}/${mdId}/dashboard`} replace />;
  }

  // If flag says "logged in" but required data is missing, reset it
  if (isCustomer && !hasEmail) {
    localStorage.removeItem("@customer");
    localStorage.removeItem("userEmail");
  }

  // Show MD login normally
  return <MDLogin />;
}

function MDDashboardGate() {
  const { mdId } = useParams();
  return isCustomerAuthenticated()
    ? <MDDashboard />
    : <Navigate to={`${basePath}/${mdId}/login`} replace />;
}

function MDUpdatePasswordGate() {
  const { mdId } = useParams();
  return isCustomerAuthenticated()
    ? <MDUpdatePassword />
    : <Navigate to={`${basePath}/${mdId}/login`} replace />;
}

function MDYubiKeyDashboardGate() {
  const { mdId } = useParams();
  return isCustomerAuthenticated()
    ? <MDYubiKeyDashboard />
    : <Navigate to={`${basePath}/${mdId}/login`} replace />;
}

function MDCreateTGate() {
  const { mdId } = useParams();
  return isCustomerAuthenticated()
    ? <MDCreateT />
    : <Navigate to={`${basePath}/${mdId}/login`} replace />;
}

function MDCreateSIGate() {
  const { mdId } = useParams();
  return isCustomerAuthenticated()
    ? <MDCreateSI />
    : <Navigate to={`${basePath}/${mdId}/login`} replace />;
}

function MDLogsGate() {
  const { mdId } = useParams();
  return isCustomerAuthenticated()
    ? <MDLogs />
    : <Navigate to={`${basePath}/${mdId}/login`} replace />;
}

function MDManagementGate() {
  const { mdId } = useParams();
  return isCustomerAuthenticated()
    ? <MDManagement />
    : <Navigate to={`${basePath}/${mdId}/login`} replace />;
}

function MDSIManagementGate() {
  const { mdId } = useParams();
  return isCustomerAuthenticated()
    ? <MDSIManagement />
    : <Navigate to={`${basePath}/${mdId}/login`} replace />;
}

function MDPanelTypeGate() {
  const { mdId } = useParams();
  return isCustomerAuthenticated()
    ? <MDPanelType/>
    : <Navigate to={`${basePath}/${mdId}/login`} replace />;
}

function MDRequestKeyGate() {
  const { mdId } = useParams();
  return isCustomerAuthenticated()
    ? <MDRequestKey/>
    : <Navigate to={`${basePath}/${mdId}/login`} replace />;
}

function MDAssignKeyGate() {
  const { mdId } = useParams();
  return isCustomerAuthenticated()
    ? <MDAssignKey/>
    : <Navigate to={`${basePath}/${mdId}/login`} replace />;
}

const Rotas = () => {

  return (
    <BrowserRouter>
      <Routes>
        <Route path={`${basePath}/login`} element={isVELOXAuthenticated() ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path={`${basePath}/register`} element={<Register />} />
        <Route path={`${basePath}/dashboard`} element={isVELOXAuthenticated() ? <Dashboard /> : <RedirectToLogin />} />
        <Route path={`${basePath}/create-user`} element={isVELOXAuthenticated() ? <CreateUser /> : <RedirectToLogin />} />
        <Route path={`${basePath}/user-management`} element={isVELOXAuthenticated() ? <UserManagement /> : <RedirectToLogin />} />
        <Route path={`${basePath}/update_password`} element={isVELOXAuthenticated() ? <UpdatePassword /> : <RedirectToLogin />} />
        <Route path={`${basePath}/velox-logs`} element={isVELOXAuthenticated() ? <VeloxLogs /> : <RedirectToLogin />} />
        <Route path={`${basePath}/test-yubikey`} element={isVELOXAuthenticated() ? <TestYubiKey /> : <RedirectToLogin />} />
        <Route path={`${basePath}/commissioningtool`} element={isVELOXAuthenticated() ? <PanelType /> : <RedirectToLogin />} />
        <Route path={`${basePath}/register-yubikey`} element={isVELOXAuthenticated() ? <RegisterYubiKey /> : <RedirectToLogin />} />
        <Route path={`${basePath}/customer-management`} element={isVELOXAuthenticated() ? <CustomerManagement /> : <RedirectToLogin />} />
        <Route path={`${basePath}/yubikey_dashboard`} element={isVELOXAuthenticated() ? <YubiKeyDashboard /> : <RedirectToLogin />} />
        <Route path={`${basePath}/messages`} element={isVELOXAuthenticated() ? <Messages /> : <RedirectToLogin />} />
        <Route path={`${basePath}/mda-review`} element={isVELOXAuthenticated() ? <MDAReview /> : <RedirectToLogin />} />
        <Route path={`${basePath}/assign-yubikey`} element={isVELOXAuthenticated() ? <AssignYubiKey /> : <RedirectToLogin />} />
        <Route path="*" element={<Navigate to={isVELOXAuthenticated() ? `${basePath}/dashboard` : `${basePath}/`} />} />
        <Route path={`${basePath}/:mdId`} element={<MDLandingPage />} />
        <Route path={`${basePath}/:mdId/login`} element={<MDLoginGate />} />
        <Route path={`${basePath}/:mdId/dashboard`} element={<MDDashboardGate />} />
        <Route path={`${basePath}/:mdId/update_password`} element={<MDUpdatePasswordGate />} />
        <Route path={`${basePath}/:mdId/yubikey_dashboard`} element={<MDYubiKeyDashboardGate />} />
        <Route path={`${basePath}/:mdId/create_t`} element={<MDCreateTGate />} />
        <Route path={`${basePath}/:mdId/create_si`} element={<MDCreateSIGate />} />
        <Route path={`${basePath}/:mdId/md_logs`} element={<MDLogsGate />} />
        <Route path={`${basePath}/:mdId/md_management`} element={<MDManagementGate />} />
        <Route path={`${basePath}/:mdId/si_management`} element={<MDSIManagementGate />} />
        <Route path={`${basePath}/:mdId/md_commissioningtool`} element={<MDPanelTypeGate />} />
        <Route path={`${basePath}/:mdId/request_cloud_key`} element={<MDRequestKeyGate />} />
        <Route path={`${basePath}/:mdId/assign_SI_cloud_key`} element={<MDAssignKeyGate />} />
        <Route path={`${basePath}/`} element={<LandingPage />} />
      </Routes>
    </BrowserRouter>
  );
};

export default Rotas;
